import type { ICurrentUser } from '@/types/auth'

/** What the session token (a JWT: "Bearer header.payload.signature") says about itself, read without the server. */
export function tokenClaims(token: string | null): { sub: string; exp: number } | null {
  const payload = token?.replace(/^Bearer\s+/i, '').split('.')[1]
  if (!payload) return null
  try {
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as { sub?: unknown; exp?: unknown }
    return typeof json.sub === 'string' && typeof json.exp === 'number' ? { sub: json.sub, exp: json.exp } : null
  } catch {
    return null
  }
}

/** Seconds a token must still have to be trusted for a first render (a clock a little ahead is fine). */
const EXPIRY_MARGIN_S = 30

/**
 * The stored user, when it can be shown before the server has confirmed the
 * session: there is a token, it has not expired, and it was issued to that
 * very user (its `sub` is the user's id). Anything else — no token, an expired
 * one, a user left over from another account — is null, and the app waits for
 * the server as it always did. The server still has the last word: a revoked
 * token answers 401 and http.service sends the page to /login.
 */
export function cachedSessionUser(token: string | null, user: ICurrentUser | null, now = Date.now()): ICurrentUser | null {
  const claims = tokenClaims(token)
  if (!claims || !user) return null
  if (claims.exp * 1000 <= now + EXPIRY_MARGIN_S * 1000) return null
  return String(user.id) === claims.sub ? user : null
}
